/**
 * Marquee for the mega-menu callouts. Past two rows of four the grid would be taller than the
 * viewport, so the callouts scroll instead — with the pause control WCAG 2.2 SC 2.2.2 requires.
 */
if (!customElements.get('custom-menu-marquee')) {
  const MARQUEE_MIN_ITEMS = 9;
  const MARQUEE_SECONDS_PER_ITEM = 5;

  // Resolved at script-evaluation time. This file is deferred, so it always runs before
  // DOMContentLoaded — a `readyState === 'loading'` test would be false here and clone the
  // callouts while the section still has the date-gated ones hidden.
  const domReady = new Promise((resolve) => {
    if (document.readyState === 'complete') resolve();
    else document.addEventListener('DOMContentLoaded', resolve, { once: true });
  });

  class CustomMenuMarquee extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      this.track = this.querySelector('.mega-menu__callouts-track');
      this.toggle = this.querySelector('.mega-menu__callouts-toggle');
      if (!this.track || !this.toggle) return;

      const { signal } = this.controller;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => this.layout(), { signal });
      this.toggle.addEventListener('click', () => this.setPaused(this.dataset.paused !== 'true'), { signal });

      // The section unhides date-gated callouts on DOMContentLoaded; duplicate the row after that.
      domReady.then(() => {
        if (!signal.aborted) this.layout();
      });
    }

    disconnectedCallback() {
      this.controller?.abort();
    }

    layout() {
      this.track.querySelectorAll('[data-marquee-clone]').forEach((clone) => clone.remove());

      const items = Array.from(this.track.children).filter((child) => child.classList.contains('menucallout'));
      if (items.length < MARQUEE_MIN_ITEMS || this.reducedMotion.matches) {
        delete this.dataset.marquee;
        this.setPaused(false);
        this.toggle.hidden = true;
        return;
      }

      items.forEach((item) => this.track.appendChild(this.duplicate(item)));
      this.style.setProperty('--custom-marquee-duration', `${items.length * MARQUEE_SECONDS_PER_ITEM}s`);
      this.dataset.marquee = 'on';
      this.toggle.hidden = false;
      this.setPaused(this.dataset.paused === 'true');
    }

    /** The second pass of items that makes the loop seamless — decorative, so hidden from both
        the accessibility tree and the tab order. */
    duplicate(item) {
      const clone = item.cloneNode(true);
      clone.removeAttribute('id');
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('data-marquee-clone', '');
      clone.querySelectorAll('a').forEach((link) => link.setAttribute('tabindex', '-1'));
      return clone;
    }

    setPaused(paused) {
      if (paused) {
        this.dataset.paused = 'true';
      } else {
        delete this.dataset.paused;
      }
      this.toggle.setAttribute('aria-label', paused ? this.toggle.dataset.labelPlay : this.toggle.dataset.labelPause);
    }
  }

  customElements.define('custom-menu-marquee', CustomMenuMarquee);
}
